import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  after,
  before,
  contains,
  documentOrder,
  findEntry,
  moveEntry,
  sectionEnd,
  sectionStart,
  stepEntry,
} from './formSections';

const FLIP_DURATION_MS = 180;

// Drag-to-reorder for the builder's tree of questions and sections.
//
// The card being dragged is reordered into the list live, as soon as the
// pointer crosses the midpoint of a neighbour, so the drop position is never a
// guess. The cards it displaces would otherwise jump to their new positions in
// a single frame, so each render replays the move as a FLIP animation: measure
// where every card was, let React paint the new order, then transform each card
// back to where it was and release it. The result is that the rest of the list
// visibly wraps around the card under the cursor.
//
// Sections nest, so the list is a tree of any depth and a drop position is
// named relative to a card ("before this one", "at the end of that section")
// rather than by index — see formSections.js, which owns what each of those
// means. A section is carried whole and lands anywhere a question could, bar
// inside itself; the one place that has to be checked, since the card it was
// dropped on would have travelled with it.
//
//   const { draggingId, entryProps, handleProps } = useEntryDragReorder({ entries, onMove, onStep });
//   <div {...entryProps(field.id)}> <span {...handleProps(field.id)} /> </div>
export const useEntryDragReorder = ({ entries, onMove, onStep }) => {
  const [draggingId, setDraggingId] = useState(null);
  const nodes = useRef(new Map());
  const previousRects = useRef(null);

  const registerNode = useCallback((id, node) => {
    if (node) nodes.current.set(id, node);
    else nodes.current.delete(id);
  }, []);

  // Snapshot positions immediately before a reorder so the next render can
  // animate from them.
  const captureRects = useCallback(() => {
    const rects = new Map();
    nodes.current.forEach((node, id) => rects.set(id, node.getBoundingClientRect().top));
    previousRects.current = rects;
  }, []);

  useLayoutEffect(() => {
    const previous = previousRects.current;
    previousRects.current = null;
    if (!previous) return;
    const moved = [];
    nodes.current.forEach((node, id) => {
      const previousTop = previous.get(id);
      if (previousTop === undefined) return;
      const delta = previousTop - node.getBoundingClientRect().top;
      if (delta) moved.push({ node, delta });
    });
    moved
      // A card inside a section that just moved has travelled with it, and the
      // section is already animating that distance — animating the card too
      // would move it twice as far as it actually went.
      .filter(({ node }) => !moved.some((other) => other.node !== node && other.node.contains(node)))
      // Animated by the browser rather than by inline styles we clear later: a
      // frame that never arrives — a backgrounded tab, a drag abandoned
      // mid-flight — can't strand a card away from its real position.
      .forEach(({ node, delta }) => {
        node.animate?.(
          [{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0px)' }],
          { duration: FLIP_DURATION_MS, easing: 'ease' },
        );
      });
  }, [entries]);

  const dragged = findEntry(entries, draggingId);

  // Whether what is in flight could come to rest at this card — beside it, or
  // inside it when it is a section. A section dropped into its own contents
  // would take the card it was dropped on out of the form with it, so those are
  // the only refusals, and every handler below turns on this one question.
  const canDropOn = (id) => !!draggingId && id !== draggingId && !contains(dragged, id);

  // Every drop position goes through here, so a target that would leave the
  // card where it already is costs nothing: formSections hands back the same
  // list, and neither a re-render nor an animation is started.
  const applyTarget = (target) => {
    if (!draggingId || !target) return;
    if (moveEntry(entries, draggingId, target) === entries) return;
    captureRects();
    onMove(draggingId, target);
  };

  const allowDrop = (event) => {
    event.preventDefault();
    // Each card answers for itself: without this a card inside a section would
    // hand the pointer straight back to the section around it.
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
  };

  // Reorder only once the pointer is past the midpoint of the card it is over,
  // in the direction of travel. Reordering on contact instead makes a tall card
  // flip back and forth as it lands under the cursor again.
  const dragOverEntry = (overId, event) => {
    // The whole card's rectangle, which for a section is the section and
    // everything in it: dropped on, that is what the pointer is beside.
    const node = nodes.current.get(overId);
    const rect = (node || event.currentTarget).getBoundingClientRect();
    const order = documentOrder(entries);
    const movingDown = order.indexOf(overId) > order.indexOf(draggingId);
    const midpoint = rect.top + rect.height / 2;
    if (movingDown ? event.clientY < midpoint : event.clientY > midpoint) return;
    applyTarget(movingDown ? after(overId) : before(overId));
  };

  const handleDragStart = (id) => (event) => {
    setDraggingId(id);
    event.dataTransfer.effectAllowed = 'move';
    // Firefox ignores a drag that carries no data.
    event.dataTransfer.setData('text/plain', id);
    // Drag the whole card, not the handle alone.
    const card = nodes.current.get(id);
    if (card && typeof event.dataTransfer.setDragImage === 'function') {
      const rect = card.getBoundingClientRect();
      event.dataTransfer.setDragImage(card, event.clientX - rect.left, event.clientY - rect.top);
    }
  };

  const handleDragEnd = () => setDraggingId(null);

  // Keyboard equivalent of the drag, and the only way to reorder on a touch
  // device, where HTML5 drag events never fire.
  const handleKeyDown = (id) => (event) => {
    const step = (event.key === 'ArrowUp' && -1) || (event.key === 'ArrowDown' && 1) || 0;
    if (!step) return;
    if (stepEntry(entries, id, step) === entries) return;
    event.preventDefault();
    captureRects();
    onStep(id, step);
  };

  const dropProps = {
    onDrop: (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDraggingId(null);
    },
  };

  // A question card, or a section as a whole.
  const entryProps = (id) => ({
    ...dropProps,
    ref: (node) => registerNode(id, node),
    onDragOver: (event) => {
      if (!canDropOn(id)) return;
      allowDrop(event);
      dragOverEntry(id, event);
    },
  });

  // The section's title row. Dropped on above the middle a card goes in front
  // of the section; below it, in at the top — the title is the one place where
  // "inside" and "before" are both a step away.
  const sectionHeaderProps = (sectionId) => ({
    ...dropProps,
    onDragOver: (event) => {
      if (!canDropOn(sectionId)) return;
      allowDrop(event);
      const rect = event.currentTarget.getBoundingClientRect();
      const above = event.clientY < rect.top + rect.height / 2;
      applyTarget(above ? before(sectionId) : sectionStart(sectionId));
    },
  });

  // The space around the cards already in a section: the gaps between them and
  // the padding at its edges. Deliberately inert — the cards themselves decide
  // where their neighbour lands, and a gap between two of them that pulled the
  // card out to the section's own level would make dragging within a section a
  // fight. It still has to claim the pointer, or the section around it would
  // answer for a gap that belongs to its contents.
  const sectionBodyProps = (sectionId) => ({
    ...dropProps,
    onDragOver: (event) => {
      if (!canDropOn(sectionId)) return;
      allowDrop(event);
    },
  });

  // The strip below a section's last card: the only way to say "inside this
  // section, at the end" rather than "after this section", and the whole of an
  // empty section's body.
  const sectionEndProps = (sectionId) => ({
    ...dropProps,
    onDragOver: (event) => {
      if (!canDropOn(sectionId)) return;
      allowDrop(event);
      applyTarget(sectionEnd(sectionId));
    },
  });

  const handleProps = (id) => ({
    draggable: true,
    onDragStart: handleDragStart(id),
    onDragEnd: handleDragEnd,
    onKeyDown: handleKeyDown(id),
  });

  return {
    draggingId,
    canDropOn,
    entryProps,
    handleProps,
    sectionHeaderProps,
    sectionBodyProps,
    sectionEndProps,
  };
};

// Drag-to-reorder for a list whose entries carry no id of their own — a
// Dropdown's options are plain strings, so an option's position is all there is
// to identify it by. The dragged position is therefore tracked as the option
// travels, in a ref rather than in state: dragover fires faster than React
// re-renders, and a stale index would reorder the wrong row.
//
// The rows are keyed by position, so the DOM nodes stay put and the values move
// through them. Nothing to animate, then — the option text visibly shuffling
// past the cursor is the feedback.
//
//   const { draggingIndex, rowProps, handleProps } = useIndexDragReorder({ count, onReorder });
//   <div {...rowProps(i)}> <span {...handleProps(i)} /> </div>
export const useIndexDragReorder = ({ count, onReorder }) => {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragging = useRef(null);
  const rows = useRef(new Map());
  const handles = useRef(new Map());

  const setDragging = (index) => {
    dragging.current = index;
    setDraggingIndex(index);
  };

  const register = (map, index) => (node) => {
    if (node) map.current.set(index, node);
    else map.current.delete(index);
  };

  const handleDragStart = (index) => (event) => {
    setDragging(index);
    event.dataTransfer.effectAllowed = 'move';
    // Firefox ignores a drag that carries no data.
    event.dataTransfer.setData('text/plain', String(index));
    // Drag the whole row, not the grip alone.
    const row = rows.current.get(index);
    if (row && typeof event.dataTransfer.setDragImage === 'function') {
      const rect = row.getBoundingClientRect();
      event.dataTransfer.setDragImage(row, event.clientX - rect.left, event.clientY - rect.top);
    }
  };

  const handleDragEnd = () => setDragging(null);

  // Same midpoint rule as the question list: move only once the pointer is past
  // the middle of the row it is over, in the direction of travel.
  const handleDragOver = (overIndex) => (event) => {
    const fromIndex = dragging.current;
    if (fromIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (overIndex === fromIndex) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const movingDown = overIndex > fromIndex;
    if (movingDown ? event.clientY < midpoint : event.clientY > midpoint) return;

    onReorder(fromIndex, overIndex);
    setDragging(overIndex);
  };

  // Keyboard equivalent of the drag, and the only way to reorder on a touch
  // device, where HTML5 drag events never fire. Focus follows the option into
  // its new row — the row it left now holds a different option, so staying put
  // would hand the next keypress to the wrong one.
  const handleKeyDown = (index) => (event) => {
    const step = (event.key === 'ArrowUp' && -1) || (event.key === 'ArrowDown' && 1) || 0;
    if (!step) return;
    const target = index + step;
    if (target < 0 || target >= count) return;
    event.preventDefault();
    onReorder(index, target);
    handles.current.get(target)?.focus();
  };

  const rowProps = (index) => ({
    ref: register(rows, index),
    onDragOver: handleDragOver(index),
    onDrop: (event) => {
      event.preventDefault();
      setDragging(null);
    },
  });

  const handleProps = (index) => ({
    ref: register(handles, index),
    draggable: true,
    onDragStart: handleDragStart(index),
    onDragEnd: handleDragEnd,
    onKeyDown: handleKeyDown(index),
  });

  return { draggingIndex, rowProps, handleProps };
};

export default useEntryDragReorder;
